import { useState, useEffect, useCallback } from 'react';
import {
  Settings, Users, Bell, Key, ClipboardList,
  Save, Loader2, Plus, Trash2, Copy, Check,
  UserPlus, Shield, AlertCircle, Plug, RefreshCw, XCircle,
  DollarSign,
} from 'lucide-react';
import { onsiteApiFetch } from '../config/api.js';

// ─── Types ────────────────────────────────────────────────────

interface SlaDefinition {
  id: string;
  name: string;
  priority: string;
  response_hours: number;
  resolution_hours: number;
}

interface OrgSettings {
  org_display_name?: string;
  timezone: string;
  distance_unit: 'miles' | 'km';
  notify_on_job_assigned: boolean;
  notify_on_job_completed: boolean;
  notify_on_checkin: boolean;
  notification_email?: string;
  require_photo_on_checkin: boolean;
  require_signature_on_completion: boolean;
  session_timeout_hours: number;
  audit_log_retention_days: number;
  job_types: string[];
  service_categories: string[];
  sla_definitions: SlaDefinition[];
  // Billing & operational
  labor_rate_per_hour?: number;
  parts_markup_percent?: number;
  tax_rate?: number;
  geofence_threshold_meters?: number;
}

interface TeamMember {
  id: string; email: string; name: string;
  org_role: string; product_role: string;
  joined_at: string; last_login_at?: string;
}

interface ApiKey {
  id: string; name: string; key_prefix: string; scopes: string[];
  created_by_email: string; last_used_at?: string;
  expires_at?: string; created_at: string;
}

interface AuditEntry {
  id: string; actor_email: string; action: string;
  action_category: string; entity_type: string;
  metadata: Record<string, unknown>; created_at: string;
}

type Tab = 'general' | 'billing' | 'job-config' | 'team' | 'notifications' | 'api-keys' | 'audit' | 'psa';

const API = '/api/v1/onsite';

function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  return onsiteApiFetch<T>(`${API}${path}`, opts);
}

const TABS: { id: Tab; label: string; icon: typeof Settings }[] = [
  { id: 'general',       label: 'General',         icon: Settings },
  { id: 'billing',       label: 'Billing',         icon: DollarSign },
  { id: 'job-config',    label: 'Job Config',      icon: ClipboardList },
  { id: 'team',          label: 'Team',            icon: Users },
  { id: 'notifications', label: 'Notifications',   icon: Bell },
  { id: 'psa',           label: 'PSA Integration', icon: Plug },
  { id: 'api-keys',      label: 'API Keys',        icon: Key },
  { id: 'audit',         label: 'Audit Log',       icon: ClipboardList },
];

function fmt(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function fmtDt(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

// ─── Main ─────────────────────────────────────────────────────

export default function SettingsPage() {
  const [tab, setTab] = useState<Tab>('general');

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your organization, team, and platform configuration.</p>
      </div>
      <div className="flex gap-1 border-b border-border mb-8">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>
      {tab === 'general' && <GeneralTab />}
      {tab === 'billing' && <BillingTab />}
      {tab === 'job-config' && <JobConfigTab />}
      {tab === 'team' && <TeamTab />}
      {tab === 'notifications' && <NotificationsTab />}
      {tab === 'psa' && <PsaTab />}
      {tab === 'api-keys' && <ApiKeysTab />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}

// ─── PSA Integration ───────────────────────────────────────────

interface PsaConfig {
  psa_type: 'connectwise' | 'halopsa' | 'none';
  base_url?: string;
  api_key?: string;
  api_key_masked?: string;
  tenant_name?: string;
  auto_sync_enabled: boolean;
  last_sync_at?: string;
  last_sync_status?: 'success' | 'failed' | 'partial' | 'running';
  last_sync_error?: string;
  import_filters?: {
    status?: string;
    board?: string;
    company?: string;
  };
}

function PsaTab() {
  const [cfg, setCfg] = useState<PsaConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');

  useEffect(() => {
    apiFetch<PsaConfig>('/psa/config')
      .then(c => { setCfg(c); setApiKeyInput(''); })
      .catch(e => {
        const msg = (e as Error).message;
        if (msg.toLowerCase().includes('not found') || msg.includes('404')) {
          setCfg({ psa_type: 'none', auto_sync_enabled: false });
        } else {
          setError(msg);
        }
      });
  }, []);

  async function save() {
    if (!cfg) return;
    setSaving(true); setError(null);
    try {
      const payload: Partial<PsaConfig> & { api_key?: string } = {
        psa_type: cfg.psa_type,
        base_url: cfg.base_url,
        tenant_name: cfg.tenant_name,
        auto_sync_enabled: cfg.auto_sync_enabled,
        import_filters: cfg.import_filters,
      };
      if (apiKeyInput) payload.api_key = apiKeyInput;
      const u = await apiFetch<PsaConfig>('/psa/config', { method: 'PATCH', body: JSON.stringify(payload) });
      setCfg(u); setApiKeyInput(''); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  async function testConnection() {
    setTesting(true); setTestResult(null);
    try {
      const r = await apiFetch<{ ok: boolean; message?: string }>('/psa/test', { method: 'POST' });
      setTestResult({ ok: !!r.ok, message: r.message || (r.ok ? 'Connection successful' : 'Connection failed') });
    } catch (e) {
      setTestResult({ ok: false, message: (e as Error).message });
    } finally { setTesting(false); }
  }

  async function syncNow() {
    setSyncing(true); setSyncResult(null);
    try {
      const r = await apiFetch<{ ok?: boolean; imported?: number; message?: string }>('/psa/sync', { method: 'POST' });
      setSyncResult({
        ok: r.ok !== false,
        message: r.message ?? (r.imported != null ? `Imported ${r.imported} items.` : 'Sync started'),
      });
      const fresh = await apiFetch<PsaConfig>('/psa/config');
      setCfg(fresh);
    } catch (e) {
      setSyncResult({ ok: false, message: (e as Error).message });
    } finally { setSyncing(false); }
  }

  if (!cfg) return <Loading />;

  return (
    <div className="space-y-8">
      {error && <ErrorBanner msg={error} />}

      <Section title="Provider">
        <Field label="PSA Type">
          <select
            className="input w-60"
            value={cfg.psa_type}
            onChange={e => setCfg({ ...cfg, psa_type: e.target.value as PsaConfig['psa_type'] })}
          >
            <option value="none">None</option>
            <option value="connectwise">ConnectWise</option>
            <option value="halopsa">HaloPSA</option>
          </select>
        </Field>
        <Field label="Base URL">
          <input
            className="input"
            placeholder="https://your-psa.example.com"
            value={cfg.base_url ?? ''}
            onChange={e => setCfg({ ...cfg, base_url: e.target.value })}
          />
        </Field>
        <Field label="Tenant Name">
          <input
            className="input"
            placeholder="acme"
            value={cfg.tenant_name ?? ''}
            onChange={e => setCfg({ ...cfg, tenant_name: e.target.value })}
          />
        </Field>
        <Field label="API Key">
          <div className="flex flex-col gap-1">
            <input
              type="password"
              className="input"
              placeholder={cfg.api_key_masked ? cfg.api_key_masked : 'Enter API key'}
              value={apiKeyInput}
              onChange={e => setApiKeyInput(e.target.value)}
            />
            {cfg.api_key_masked && !apiKeyInput && (
              <p className="text-xs text-muted-foreground">Current: {cfg.api_key_masked} — leave blank to keep</p>
            )}
          </div>
        </Field>
      </Section>

      <Section title="Connection">
        <div className="flex items-center gap-3">
          <button onClick={testConnection} disabled={testing || cfg.psa_type === 'none'} className="btn-primary">
            {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plug className="w-4 h-4 mr-2" />}
            Test Connection
          </button>
          <button onClick={syncNow} disabled={syncing || cfg.psa_type === 'none'} className="btn-ghost">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync Now
          </button>
        </div>
        {testResult && (
          <div className={`mt-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${testResult.ok ? 'border-green-800 bg-green-950 text-green-300' : 'border-red-800 bg-red-950 text-red-300'}`}>
            {testResult.ok ? <Check className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {testResult.message}
          </div>
        )}
        {syncResult && (
          <div className={`mt-2 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${syncResult.ok ? 'border-green-800 bg-green-950 text-green-300' : 'border-red-800 bg-red-950 text-red-300'}`}>
            {syncResult.ok ? <Check className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
            {syncResult.message}
          </div>
        )}
        {cfg.last_sync_at && (
          <p className="mt-2 text-xs text-muted-foreground">
            Last sync: {fmtDt(cfg.last_sync_at)}
            {cfg.last_sync_status && <> · <span className="capitalize">{cfg.last_sync_status}</span></>}
            {cfg.last_sync_error && <> · <span className="text-red-400">{cfg.last_sync_error}</span></>}
          </p>
        )}
      </Section>

      <Section title="Automation">
        <Toggle
          label="Auto-sync"
          description="Automatically pull new PSA tickets every 15 minutes."
          checked={cfg.auto_sync_enabled}
          onChange={v => setCfg({ ...cfg, auto_sync_enabled: v })}
        />
      </Section>

      <Section title="Import Filters">
        <Field label="Status">
          <input
            className="input"
            placeholder="open, scheduled"
            value={cfg.import_filters?.status ?? ''}
            onChange={e => setCfg({ ...cfg, import_filters: { ...(cfg.import_filters ?? {}), status: e.target.value } })}
          />
        </Field>
        <Field label="Board">
          <input
            className="input"
            placeholder="Field Service"
            value={cfg.import_filters?.board ?? ''}
            onChange={e => setCfg({ ...cfg, import_filters: { ...(cfg.import_filters ?? {}), board: e.target.value } })}
          />
        </Field>
        <Field label="Company">
          <input
            className="input"
            placeholder="All companies or comma-separated list"
            value={cfg.import_filters?.company ?? ''}
            onChange={e => setCfg({ ...cfg, import_filters: { ...(cfg.import_filters ?? {}), company: e.target.value } })}
          />
        </Field>
      </Section>

      <div className="flex justify-end"><SaveBtn saving={saving} saved={saved} onClick={save} /></div>
    </div>
  );
}

// ─── General ──────────────────────────────────────────────────

function GeneralTab() {
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { apiFetch<OrgSettings>('/settings').then(setSettings).catch(e => setError((e as Error).message)); }, []);

  async function save() {
    if (!settings) return;
    setSaving(true); setError(null);
    try {
      const u = await apiFetch<OrgSettings>('/settings', { method: 'PATCH', body: JSON.stringify(settings) });
      setSettings(u); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  if (!settings) return <Loading />;

  return (
    <div className="space-y-8">
      {error && <ErrorBanner msg={error} />}
      <Section title="Organization">
        <Field label="Display Name">
          <input className="input" value={settings.org_display_name ?? ''} onChange={e => setSettings({ ...settings, org_display_name: e.target.value })} placeholder="Acme Field Services" />
        </Field>
        <Field label="Timezone">
          <select className="input" value={settings.timezone} onChange={e => setSettings({ ...settings, timezone: e.target.value })}>
            {['UTC','America/New_York','America/Chicago','America/Denver','America/Los_Angeles','Europe/London','Australia/Sydney'].map(tz => <option key={tz}>{tz}</option>)}
          </select>
        </Field>
        <Field label="Distance Unit">
          <select className="input w-40" value={settings.distance_unit} onChange={e => setSettings({ ...settings, distance_unit: e.target.value as 'miles' | 'km' })}>
            <option value="miles">Miles</option>
            <option value="km">Kilometers</option>
          </select>
        </Field>
      </Section>

      <Section title="Field Requirements">
        <Toggle label="Require photo on check-in" checked={settings.require_photo_on_checkin} onChange={v => setSettings({ ...settings, require_photo_on_checkin: v })} />
        <Toggle label="Require signature on job completion" checked={settings.require_signature_on_completion} onChange={v => setSettings({ ...settings, require_signature_on_completion: v })} />
      </Section>

      <Section title="Security">
        <Field label="Session Timeout (hours)">
          <input type="number" min={1} max={168} className="input w-32" value={settings.session_timeout_hours} onChange={e => setSettings({ ...settings, session_timeout_hours: parseInt(e.target.value) || 8 })} />
        </Field>
        <Field label="Audit Log Retention (days)">
          <input type="number" min={30} max={3650} className="input w-32" value={settings.audit_log_retention_days} onChange={e => setSettings({ ...settings, audit_log_retention_days: parseInt(e.target.value) || 90 })} />
        </Field>
      </Section>

      <div className="flex justify-end">
        <SaveBtn saving={saving} saved={saved} onClick={save} />
      </div>
    </div>
  );
}

// ─── Billing & Operations ────────────────────────────────────

function BillingTab() {
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<OrgSettings>('/settings').then(setSettings).catch(e => setError((e as Error).message));
  }, []);

  async function save() {
    if (!settings) return;
    setSaving(true); setError(null);
    try {
      const u = await apiFetch<OrgSettings>('/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          labor_rate_per_hour: settings.labor_rate_per_hour,
          parts_markup_percent: settings.parts_markup_percent,
          tax_rate: settings.tax_rate,
          geofence_threshold_meters: settings.geofence_threshold_meters,
        }),
      });
      setSettings(u); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  if (!settings) return <Loading />;

  return (
    <div className="space-y-8">
      {error && <ErrorBanner msg={error} />}

      <Section title="Labor & Parts Pricing">
        <p className="text-xs text-muted-foreground mb-2">
          These values drive every invoice generated from a job. Changes only
          affect new invoices — existing invoices are not re-priced.
        </p>
        <Field label="Labor Rate ($/hr)">
          <input
            type="number" min={0} step={0.01}
            className="input w-40"
            value={settings.labor_rate_per_hour ?? 0}
            onChange={e => setSettings({ ...settings, labor_rate_per_hour: parseFloat(e.target.value) || 0 })}
            placeholder="150.00"
          />
        </Field>
        <Field label="Parts Markup (%)">
          <input
            type="number" min={0} max={500} step={0.1}
            className="input w-40"
            value={settings.parts_markup_percent ?? 0}
            onChange={e => setSettings({ ...settings, parts_markup_percent: parseFloat(e.target.value) || 0 })}
            placeholder="25"
          />
        </Field>
        <Field label="Tax Rate (%)">
          <input
            type="number" min={0} max={100} step={0.01}
            className="input w-40"
            value={settings.tax_rate ?? 0}
            onChange={e => setSettings({ ...settings, tax_rate: parseFloat(e.target.value) || 0 })}
            placeholder="0"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Applied to (labor + parts) subtotal. Leave at 0 if taxes are handled downstream.
          </p>
        </Field>
      </Section>

      <Section title="Geofence">
        <Field label="Check-in Threshold (meters)">
          <input
            type="number" min={10} max={50000} step={10}
            className="input w-40"
            value={settings.geofence_threshold_meters ?? 500}
            onChange={e => setSettings({ ...settings, geofence_threshold_meters: parseInt(e.target.value) || 500 })}
            placeholder="500"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Technicians arriving more than this distance from the job site must
            provide a manual override. Default is 500m (~1/3 mile).
          </p>
        </Field>
      </Section>

      <div className="flex justify-end"><SaveBtn saving={saving} saved={saved} onClick={save} /></div>
    </div>
  );
}

// ─── Job Config ──────────────────────────────────────────────

function JobConfigTab() {
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { apiFetch<OrgSettings>('/settings').then(setSettings).catch(e => setError((e as Error).message)); }, []);

  async function save() {
    if (!settings) return;
    setSaving(true); setError(null);
    try {
      const u = await apiFetch<OrgSettings>('/settings', { method: 'PATCH', body: JSON.stringify({ job_types: settings.job_types, service_categories: settings.service_categories, sla_definitions: settings.sla_definitions }) });
      setSettings(u); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  if (!settings) return <Loading />;

  const jobTypes = settings.job_types ?? [];
  const categories = settings.service_categories ?? [];
  const slas = settings.sla_definitions ?? [];

  return (
    <div className="space-y-8">
      {error && <ErrorBanner msg={error} />}
      <Section title="Job Types">
        <p className="text-xs text-muted-foreground mb-2">Comma-separated list of job types available when creating jobs.</p>
        <input
          className="input"
          value={jobTypes.join(', ')}
          onChange={e => setSettings({ ...settings, job_types: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
          placeholder="installation, repair, maintenance, break_fix, project, assessment, other"
        />
      </Section>

      <Section title="Service Categories">
        <p className="text-xs text-muted-foreground mb-2">Categories for organizing jobs and reporting.</p>
        <input
          className="input"
          value={categories.join(', ')}
          onChange={e => setSettings({ ...settings, service_categories: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
          placeholder="Networking, Server, Workstation, Printer, Phone System, Security, Cloud, Other"
        />
      </Section>

      <Section title="SLA Definitions">
        <p className="text-xs text-muted-foreground mb-3">Response and resolution time targets per priority level.</p>
        <div className="space-y-3">
          {slas.map((sla, i) => (
            <div key={sla.id} className="grid grid-cols-4 gap-3 items-end">
              <div>
                <label className="text-xs text-muted-foreground">Name</label>
                <input className="input" value={sla.name} onChange={e => {
                  const updated = [...slas]; updated[i] = { ...sla, name: e.target.value };
                  setSettings({ ...settings, sla_definitions: updated });
                }} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Priority</label>
                <input className="input" value={sla.priority} readOnly />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Response (hours)</label>
                <input type="number" min={0} className="input" value={sla.response_hours} onChange={e => {
                  const updated = [...slas]; updated[i] = { ...sla, response_hours: parseInt(e.target.value) || 0 };
                  setSettings({ ...settings, sla_definitions: updated });
                }} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Resolution (hours)</label>
                <input type="number" min={0} className="input" value={sla.resolution_hours} onChange={e => {
                  const updated = [...slas]; updated[i] = { ...sla, resolution_hours: parseInt(e.target.value) || 0 };
                  setSettings({ ...settings, sla_definitions: updated });
                }} />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="flex justify-end"><SaveBtn saving={saving} saved={saved} onClick={save} /></div>
    </div>
  );
}

// ─── Team ─────────────────────────────────────────────────────

function TeamTab() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', product_role: 'viewer' });
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch<{ data: TeamMember[] }>('/team').then(r => setMembers(r.data)).catch(e => setError((e as Error).message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function invite() {
    setInviting(true); setError(null);
    try {
      await apiFetch('/team/invite', { method: 'POST', body: JSON.stringify(form) });
      setShowInvite(false); setForm({ email: '', name: '', product_role: 'viewer' }); load();
    } catch (e) { setError((e as Error).message); }
    finally { setInviting(false); }
  }

  async function updateRole(id: string, role: string) {
    try {
      await apiFetch(`/team/${id}/role`, { method: 'PATCH', body: JSON.stringify({ product_role: role }) });
      setMembers(prev => prev.map(m => m.id === id ? { ...m, product_role: role } : m));
    } catch (e) { setError((e as Error).message); }
  }

  async function remove(id: string) {
    if (!confirm('Remove this member\'s access?')) return;
    try {
      await apiFetch(`/team/${id}`, { method: 'DELETE' });
      setMembers(prev => prev.filter(m => m.id !== id));
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="space-y-6">
      {error && <ErrorBanner msg={error} />}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Team members are managed through Hub.</p>
        <button onClick={() => setShowInvite(!showInvite)} className="btn-primary"><UserPlus className="w-4 h-4 mr-2" />Invite Member</button>
      </div>
      {showInvite && (
        <div className="p-4 bg-card border border-border rounded-lg space-y-3">
          <h3 className="text-sm font-medium text-foreground">Invite Team Member</h3>
          <div className="grid grid-cols-3 gap-3">
            <input className="input" placeholder="Email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
            <input className="input" placeholder="Name (optional)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            <select className="input" value={form.product_role} onChange={e => setForm({ ...form, product_role: e.target.value })}>
              <option value="admin">Admin</option>
              <option value="dispatcher">Dispatcher</option>
              <option value="tech">Technician</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowInvite(false)} className="btn-ghost">Cancel</button>
            <button onClick={invite} disabled={inviting || !form.email} className="btn-primary">
              {inviting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}Send Invite
            </button>
          </div>
        </div>
      )}
      {loading ? <Loading /> : (
        <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
          <thead><tr className="bg-card border-b border-border">
            <th className="text-left px-4 py-3 text-muted-foreground font-medium">Member</th>
            <th className="text-left px-4 py-3 text-muted-foreground font-medium">Role</th>
            <th className="text-left px-4 py-3 text-muted-foreground font-medium">Last Login</th>
            <th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id} className="border-b border-border last:border-0 hover:bg-card/50">
                <td className="px-4 py-3"><div className="font-medium text-foreground">{m.name}</div><div className="text-muted-foreground text-xs">{m.email}</div></td>
                <td className="px-4 py-3">
                  <select className="text-xs bg-card border border-border rounded px-2 py-1 text-foreground" value={m.product_role} onChange={e => updateRole(m.id, e.target.value)}>
                    <option value="admin">Admin</option>
                    <option value="dispatcher">Dispatcher</option>
                    <option value="tech">Technician</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{fmt(m.last_login_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(m.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Notifications ────────────────────────────────────────────

function NotificationsTab() {
  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { apiFetch<OrgSettings>('/settings').then(setSettings).catch(e => setError((e as Error).message)); }, []);

  async function save() {
    if (!settings) return;
    setSaving(true); setError(null);
    try {
      const u = await apiFetch<OrgSettings>('/settings', { method: 'PATCH', body: JSON.stringify({ notify_on_job_assigned: settings.notify_on_job_assigned, notify_on_job_completed: settings.notify_on_job_completed, notify_on_checkin: settings.notify_on_checkin, notification_email: settings.notification_email }) });
      setSettings(u); setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  }

  if (!settings) return <Loading />;

  return (
    <div className="space-y-8">
      {error && <ErrorBanner msg={error} />}
      <Section title="Notification Email">
        <Field label="Alert Email">
          <input type="email" className="input" placeholder="alerts@company.com" value={settings.notification_email ?? ''} onChange={e => setSettings({ ...settings, notification_email: e.target.value })} />
        </Field>
      </Section>
      <Section title="Alert Triggers">
        <Toggle label="Job assigned" description="Alert when a job is assigned to a technician." checked={settings.notify_on_job_assigned} onChange={v => setSettings({ ...settings, notify_on_job_assigned: v })} />
        <Toggle label="Job completed" description="Alert when a technician completes a job." checked={settings.notify_on_job_completed} onChange={v => setSettings({ ...settings, notify_on_job_completed: v })} />
        <Toggle label="Check-in" description="Alert when a technician checks in at a job site." checked={settings.notify_on_checkin} onChange={v => setSettings({ ...settings, notify_on_checkin: v })} />
      </Section>
      <div className="flex justify-end"><SaveBtn saving={saving} saved={saved} onClick={save} /></div>
    </div>
  );
}

// ─── API Keys ─────────────────────────────────────────────────

function ApiKeysTab() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: '', scopes: ['read'] as string[], expires_in_days: '' });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ data: ApiKey[] }>('/api-keys').then(r => setKeys(r.data)).catch(e => setError((e as Error).message)).finally(() => setLoading(false));
  }, []);

  async function create() {
    setCreating(true); setError(null);
    try {
      const r = await apiFetch<ApiKey & { key: string }>('/api-keys', { method: 'POST', body: JSON.stringify({ name: form.name, scopes: form.scopes, ...(form.expires_in_days ? { expires_in_days: parseInt(form.expires_in_days) } : {}) }) });
      setNewKey(r.key); setKeys(prev => [r, ...prev]); setShowCreate(false); setForm({ name: '', scopes: ['read'], expires_in_days: '' });
    } catch (e) { setError((e as Error).message); }
    finally { setCreating(false); }
  }

  async function revoke(id: string) {
    if (!confirm('Revoke this API key?')) return;
    try {
      await apiFetch(`/api-keys/${id}`, { method: 'DELETE' });
      setKeys(prev => prev.filter(k => k.id !== id));
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="space-y-6">
      {error && <ErrorBanner msg={error} />}
      {newKey && (
        <div className="p-4 bg-green-950 border border-green-800 rounded-lg">
          <p className="text-sm font-medium text-green-300 mb-2"><Shield className="w-4 h-4 inline mr-1" />Key created — copy it now.</p>
          <div className="flex items-center gap-2 font-mono text-xs bg-background border border-border rounded px-3 py-2">
            <span className="flex-1 text-green-400 truncate">{newKey}</span>
            <button onClick={() => { navigator.clipboard.writeText(newKey); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="text-muted-foreground hover:text-foreground">
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <button onClick={() => setNewKey(null)} className="text-xs text-muted-foreground mt-2 hover:text-foreground">Dismiss</button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">API keys provide programmatic access.</p>
        <button onClick={() => setShowCreate(!showCreate)} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Create Key</button>
      </div>
      {showCreate && (
        <div className="p-4 bg-card border border-border rounded-lg space-y-3">
          <h3 className="text-sm font-medium text-foreground">New API Key</h3>
          <input className="input" placeholder="Name (e.g. Integration)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <div className="flex gap-3">
            {['read','write','webhooks'].map(s => (
              <label key={s} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                <input type="checkbox" checked={form.scopes.includes(s)} onChange={e => setForm({ ...form, scopes: e.target.checked ? [...form.scopes, s] : form.scopes.filter(x => x !== s) })} />
                <span className="capitalize">{s}</span>
              </label>
            ))}
          </div>
          <input type="number" min={1} max={3650} className="input w-40" placeholder="Expires in days (optional)" value={form.expires_in_days} onChange={e => setForm({ ...form, expires_in_days: e.target.value })} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button onClick={create} disabled={creating || !form.name || !form.scopes.length} className="btn-primary">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Create
            </button>
          </div>
        </div>
      )}
      {loading ? <Loading /> : keys.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground"><Key className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No API keys yet.</p></div>
      ) : (
        <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
          <thead><tr className="bg-card border-b border-border">
            <th className="text-left px-4 py-3 text-muted-foreground font-medium">Name</th>
            <th className="text-left px-4 py-3 text-muted-foreground font-medium">Prefix</th>
            <th className="text-left px-4 py-3 text-muted-foreground font-medium">Scopes</th>
            <th className="text-left px-4 py-3 text-muted-foreground font-medium">Expires</th>
            <th className="px-4 py-3" />
          </tr></thead>
          <tbody>
            {keys.map(k => (
              <tr key={k.id} className="border-b border-border last:border-0 hover:bg-card/50">
                <td className="px-4 py-3 font-medium text-foreground">{k.name}</td>
                <td className="px-4 py-3 font-mono text-muted-foreground text-xs">{k.key_prefix}…</td>
                <td className="px-4 py-3"><div className="flex gap-1">{k.scopes.map(s => <span key={s} className="px-1.5 py-0.5 bg-secondary text-foreground text-xs rounded">{s}</span>)}</div></td>
                <td className="px-4 py-3 text-muted-foreground">{k.expires_at ? fmt(k.expires_at) : 'Never'}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => revoke(k.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Audit Log ────────────────────────────────────────────────

function AuditTab() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [token, setToken] = useState<string | undefined>();
  const [nextToken, setNextToken] = useState<string | undefined>();

  const load = useCallback((cat: string, ct?: string) => {
    setLoading(true);
    const p = new URLSearchParams({ limit: '50' });
    if (cat) p.set('category', cat);
    if (ct) p.set('continuation_token', ct);
    apiFetch<{ data: AuditEntry[]; continuation_token?: string }>(`/audit-log?${p}`)
      .then(r => { setEntries(r.data); setNextToken(r.continuation_token); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(category); }, [load, category]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm text-muted-foreground">Category:</label>
        <select className="text-xs bg-card border border-border rounded px-2 py-1 text-foreground" value={category} onChange={e => { setCategory(e.target.value); setToken(undefined); }}>
          {['', 'config', 'team', 'auth', 'data', 'job', 'dispatch', 'client', 'technician', 'billing'].map(c => <option key={c} value={c}>{c || 'All'}</option>)}
        </select>
      </div>
      {loading ? <Loading /> : entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground"><ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No audit log entries.</p></div>
      ) : (
        <>
          <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
            <thead><tr className="bg-card border-b border-border">
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Time</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Actor</th>
              <th className="text-left px-4 py-3 text-muted-foreground font-medium">Action</th>
            </tr></thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-b border-border last:border-0 hover:bg-card/50">
                  <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{fmtDt(e.created_at)}</td>
                  <td className="px-4 py-3 text-foreground text-xs">{e.actor_email || '(system)'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-primary">{e.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {nextToken && (
            <div className="flex justify-center">
              <button onClick={() => { setToken(nextToken); load(category, nextToken); }} className="btn-ghost">Load More</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Shared UI ────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <label className="w-52 shrink-0 text-sm text-muted-foreground pt-2">{label}</label>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Toggle({ label, description, checked, onChange }: { label: string; description?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div>
        <div className="text-sm text-foreground">{label}</div>
        {description && <div className="text-xs text-muted-foreground mt-0.5">{description}</div>}
      </div>
      <button role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-secondary'}`}>
        <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${checked ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

function SaveBtn({ saving, saved, onClick }: { saving: boolean; saved: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} disabled={saving} className="btn-primary">
      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : saved ? <Check className="w-4 h-4 mr-2 text-green-400" /> : <Save className="w-4 h-4 mr-2" />}
      {saved ? 'Saved' : 'Save Changes'}
    </button>
  );
}

function Loading() {
  return (
    <div className="flex items-center justify-center h-48 text-muted-foreground">
      <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…
    </div>
  );
}

function ErrorBanner({ msg }: { msg: string }) {
  return (
    <div className="flex items-center gap-2 p-3 bg-red-950 border border-red-800 rounded text-red-300 text-sm">
      <AlertCircle className="w-4 h-4 shrink-0" />{msg}
    </div>
  );
}
